import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DataElement, ProcessRunStatusEnum } from '@shared/models/common';
import { PageToolRevImage, PageToolRevPhrase } from '@shared/models/rev.model';
import { NgRedux } from '@shared/redux/redux-compatibility.service';
import { actionsList, AppState } from '@shared/redux/store';
import { AuthService } from '@shared/services/auth.service';
import { CommonService } from '@shared/services/common.service';
import { RevService } from '@shared/services/rev.service';

@Component({
    selector: 'page-tool',
    templateUrl: './page-tool.component.html',
    styleUrl: './page-tool.component.scss',
    standalone: false
})
export class PageToolComponent implements OnInit {

  @Output() onClose = new EventEmitter();
  @Output() onSave = new EventEmitter();
  @Input() docId: number;
  @Input() pageNum: number;
  loading: boolean;
  showManagePhrases: boolean = false;
  showManageImages: boolean = false;
  phrases: PageToolRevPhrase[] = [];
  images: PageToolRevImage[] = [];
  selectedPhrases: number[] = [];
  selectedImages: number[] = [];
  contentTypes: DataElement[] = [];
  selectedContentType?: DataElement;
  defaultContentType: DataElement = new DataElement(null, this.translateService.instant('translate.rev.pageToolContentTypeExisting'));
  processStatusCheckIntervalMillis = 1000;

  constructor(
    private revService: RevService, 
    private ngRedux: NgRedux<AppState>, 
    private translateService: TranslateService,
    private commonService: CommonService,
    private authService: AuthService
  ) {}  

  ngOnInit(): void {
    this.loading = true;
    this.contentTypes = [...this.ngRedux.getState().contentTypeList];
    this.contentTypes.push(this.defaultContentType);

    this.selectedContentType = this.defaultContentType;

    this.revService.getRevDocPagePhrases(this.docId, this.pageNum - 1).subscribe(
      (phrases) => {
        this.loading = false;
        this.phrases = phrases;

        this.phrases = this.phrases.filter(phrase => !phrase.has_rev_rule);
        
        this.phrases.sort((a, b) => {
          if (a.page_order_id !== b.page_order_id) {
            return a.page_order_id - b.page_order_id;
          }
          return a.phrase_id - b.phrase_id;
        });

        this.phrases.forEach(phrase => {
          phrase.phrase_content_type = this.contentTypes.find(ct => ct.id === phrase.phrase_content_type)?.text || phrase.phrase_content_type;
        })
      },
      (error) => {
        this.loading = false;
        console.error('Error fetching phrases for page', this.pageNum, ':', error);
      }
    );

    this.revService.getRevDocPageImages(this.docId, this.pageNum - 1).subscribe(
      (images) => {
        this.loading = false;
        this.images = images;

        this.images = this.images.filter(image => !image.has_rev_rule);
        
        this.images.sort((a, b) => {
          if (a.page_order_id !== b.page_order_id) {
            return a.page_order_id - b.page_order_id;
          }
          return a.image_id - b.image_id;
        });

        this.images.forEach(image => {
          image.figure_type = this.contentTypes.find(ct => ct.id === image.figure_type)?.text || image.figure_type;
        })
      },
      (error) => {
        this.loading = false;
        console.error('Error fetching images for page', this.pageNum, ':', error);
      }
    );
  }

  onPhraseSelected(checked: boolean, phrase: PageToolRevPhrase) {
    if (checked) {
      this.selectedPhrases.push(phrase.phrase_id);
    } else {
      this.selectedPhrases = this.selectedPhrases.filter(id => phrase.phrase_id != id);
    }
  }

  onImageSelected(checked: boolean, image: PageToolRevImage) {
    if (checked) {
      this.selectedImages.push(image.image_id);
    } else {
      this.selectedImages = this.selectedImages.filter(id => image.image_id != id);
    }
  }

  selectContentType(contentType: DataElement) {
    this.selectedContentType = contentType;
  }

  close() {
    this.onClose.emit();
  }

  saveChanges() {
    if (this.selectedPhrases.length > 0 || this.selectedImages.length > 0) {
      this.loading = true;

      this.commonService.addAlgRunHead({
        doc_id: this.docId, 
        run_type: 'DOC_REV_REVALUATE', 
        env_id: this.ngRedux.getState().curEnv.id, 
        cat_version_id: this.ngRedux.getState().curRevDoc.catVersionId}
      ).subscribe(
        runNum => {
          this.revService.revealPhrasesAndImages(runNum, this.docId, this.selectedPhrases, this.selectedContentType.id, this.selectedImages)
            .subscribe(
              _ => {
                let timer = setInterval(() => {
                  this.authService.getProcessStatus(runNum).subscribe(
                    (data) => {
                      if (data.status == ProcessRunStatusEnum.FINISHED || data.status == ProcessRunStatusEnum.FAILED) {
                        clearInterval(timer);
                        this.loading = false;

                        this.ngRedux.dispatch({
                          type: actionsList.REMOVE_CURRENT_ANNOTATIONS
                        });
                        this.onSave.emit();
                      }
                    },
                    (err) => {
                      this.loading = false;
                      clearInterval(timer);
                    }
                  );
                }, this.processStatusCheckIntervalMillis);
              }
            )
        },
        err => {
          this.loading = false;
        }
      );      
    } else {
      this.close();
    }
  }

}
